<template>
  <div>
    <b-card>
      <b-card-header>
        <b-card-title class="mb-0">Staff Listing</b-card-title>
      </b-card-header>

      <b-card-body>
        <b-card class="filter-panel mb-2">
          <b-row>
            <b-col cols="12" md="6" lg="4" xl="2" class="mb-1">
              <label class="filter-label">Companies</label>
              <v-select :options="companies" :multiple="true" label="label" v-model="filters.company" @input="onCompanyChange" placeholder="All" />
              <b-button size="sm" variant="link" class="p-0 mt-25" @click="selectAllCompanies">Select All</b-button>
            </b-col>
            <b-col cols="12" md="6" lg="4" xl="2" class="mb-1">
              <label class="filter-label">Sites</label>
              <v-select :options="filteredSites" :multiple="true" label="label" v-model="filters.site" placeholder="All" />
              <b-button size="sm" variant="link" class="p-0 mt-25" @click="selectAllSites">Select All</b-button>
            </b-col>
            <b-col cols="12" md="6" lg="4" xl="2" class="mb-1">
              <label class="filter-label">Roles</label>
              <v-select :options="roles" :multiple="true" label="label" v-model="filters.role" placeholder="All" />
              <b-button size="sm" variant="link" class="p-0 mt-25" @click="selectAllRoles">Select All</b-button>
            </b-col>
            <b-col cols="12" md="6" lg="4" xl="2" class="mb-1">
              <label class="filter-label">Manager</label>
              <v-select :options="filteredManagers" :multiple="true" label="label" v-model="filters.manager" placeholder="All" />
            </b-col>
            <b-col cols="12" md="6" lg="4" xl="2" class="mb-1">
              <label class="filter-label">Status</label>
              <v-select :options="statuses" :multiple="true" label="label" v-model="filters.status" placeholder="Active" />
            </b-col>
            <b-col cols="12" md="6" lg="4" xl="2" class="mb-1">
              <label class="filter-label">Search</label>
              <b-form-input v-model="filters.search" placeholder="Name or email" debounce="400" />
            </b-col>
          </b-row>

          <b-row class="mt-1">
            <b-col cols="12">
              <b-button size="sm" variant="outline-secondary" class="mr-1" @click="resetFilters">Reset Filters</b-button>
              <b-button size="sm" variant="outline-primary" class="mr-1" @click="applyPreset('regional_managers')">Regional Managers</b-button>
              <b-button size="sm" variant="outline-primary" class="mr-1" @click="applyPreset('development_managers')">Development Managers</b-button>
            </b-col>
          </b-row>
        </b-card>

        <b-card>
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h5 class="mb-0">
              {{ total }} {{ total === 1 ? 'person' : 'people' }}
            </h5>
            <b-button size="sm" variant="success" :disabled="loading || !total" @click="doExport">Export</b-button>
          </div>

          <div v-if="loading" class="text-center py-3">
            <b-spinner small class="mr-1" /> Loading...
          </div>

          <div v-else-if="!total" class="text-center text-muted py-3">
            No staff match these filters.
          </div>

          <div v-else class="table-responsive" style="max-height:600px; overflow:auto;">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Company</th>
                  <th>Site(s)</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Manager(s)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in rows" :key="row.id">
                  <td>{{ row.name }}</td>
                  <td>{{ row.role || '-' }}</td>
                  <td>{{ row.company || '-' }}</td>
                  <td>
                    <span v-if="row.sites && row.sites.length">
                      <b-badge v-for="s in row.sites" :key="s" variant="light-primary" class="mr-25">{{ s }}</b-badge>
                    </span>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <a v-if="row.email" :href="`mailto:${row.email}`">{{ row.email }}</a>
                    <span v-else>-</span>
                  </td>
                  <td>{{ row.phone || '-' }}</td>
                  <td>
                    <span v-if="row.managers && row.managers.length">{{ row.managers.join(', ') }}</span>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <b-badge :variant="row.status === 'active' ? 'light-success' : 'light-secondary'">
                      {{ row.status === 'active' ? 'Active' : 'Inactive' }}
                    </b-badge>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-card>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import store from '@/store'
import reportStoreModule from './reportStoreModule'
import vSelect from 'vue-select'

export default {
  name: 'StaffListing',
  components: { vSelect },
  data() {
    return {
      companies: [],
      sites: [],
      roles: [],
      managers: [],
      statuses: [],
      filters: {
        company: [],
        site: [],
        role: [],
        manager: [],
        status: [],
        search: '',
      },
      rows: [],
      total: 0,
      loading: false,
      loadTimer: null,
    }
  },
  computed: {
    filteredSites() {
      const ids = (this.filters.company || []).map(c => c.id)
      if (!ids.length) return this.sites
      return this.sites.filter(s => ids.includes(s.company_id))
    },
    filteredManagers() {
      const ids = (this.filters.company || []).map(c => c.id)
      if (!ids.length) return this.managers
      return this.managers.filter(m => ids.includes(m.company_id))
    },
  },
  watch: {
    // One deep watcher drives every reload. Previously each control carried its
    // own @input handler, which meant a control whose event did not fire left
    // the list stale. Any change to any filter now reloads.
    filters: {
      handler() { this.queueLoad() },
      deep: true,
    },
  },
  created() {
    const MODULE = 'trainingReports'
    if (!store.hasModule(MODULE)) store.registerModule(MODULE, reportStoreModule)
    this.loadOptions().then(() => this.loadData())
  },
  beforeDestroy() {
    clearTimeout(this.loadTimer)
  },
  methods: {
    // Collapse the burst of changes a single interaction can produce into one request
    queueLoad() {
      clearTimeout(this.loadTimer)
      this.loadTimer = setTimeout(() => this.loadData(), 250)
    },
    loadOptions() {
      return store.dispatch('trainingReports/getStaffOptions').then(res => {
        this.companies = res.data.companies || []
        this.sites = res.data.sites || []
        this.roles = res.data.roles || []
        this.managers = res.data.managers || []
        this.statuses = res.data.statuses || []
      }).catch(err => {
        this.$swal({ title: 'Error', text: 'Failed to load filters: ' + err, icon: 'error' })
      })
    },
    // Drop site/manager selections that no longer belong to the chosen
    // companies. Only reassigns when something actually changed, otherwise the
    // deep watcher would see a new array every time and loop.
    onCompanyChange() {
      const siteIds = this.filteredSites.map(s => s.id)
      const site = (this.filters.site || []).filter(s => siteIds.includes(s.id))
      if (site.length !== (this.filters.site || []).length) this.filters.site = site

      const managerIds = this.filteredManagers.map(m => m.id)
      const manager = (this.filters.manager || []).filter(m => managerIds.includes(m.id))
      if (manager.length !== (this.filters.manager || []).length) this.filters.manager = manager
    },
    selectAllCompanies() {
      this.filters.company = (this.companies || []).slice()
    },
    selectAllSites() {
      this.filters.site = this.filteredSites.slice()
    },
    selectAllRoles() {
      this.filters.role = (this.roles || []).slice()
    },
    resetFilters() {
      this.filters = { company: [], site: [], role: [], manager: [], status: [], search: '' }
    },
    applyPreset(preset) {
      const wanted = preset === 'regional_managers' ? 'regional manager' : 'development manager'
      const match = (this.roles || []).filter(r => String(r.label).toLowerCase() === wanted)
      if (!match.length) {
        this.$swal({
          title: 'No match',
          text: 'No users currently have that role recorded.',
          icon: 'info',
        })
        return
      }
      this.filters.company = []
      this.filters.site = []
      this.filters.manager = []
      this.filters.role = match
    },
    buildParams() {
      const company_id = (this.filters.company || []).map(c => c.id).join(',') || undefined
      const site_id = (this.filters.site || []).map(s => s.id).join(',') || undefined
      const role = (this.filters.role || []).map(r => r.id).join(',') || undefined
      const manager_id = (this.filters.manager || []).map(m => m.id).join(',') || undefined
      const status = (this.filters.status || []).map(s => s.id).join(',') || undefined
      const search = (this.filters.search || '').trim() || undefined
      return { company_id, site_id, role, manager_id, status, search }
    },
    loadData() {
      this.loading = true
      store.dispatch('trainingReports/getStaffListing', this.buildParams()).then(res => {
        this.rows = res.data.rows || []
        this.total = res.data.total || 0
      }).catch(err => {
        this.$swal({ title: 'Error', text: 'Failed to load staff listing: ' + err, icon: 'error' })
      }).finally(() => {
        this.loading = false
      })
    },
    doExport() {
      store.dispatch('trainingReports/exportStaffListing', this.buildParams()).then(res => {
        this.saveBlobResponse(res)
      }).catch(err => {
        this.$swal({ title: 'Error', text: 'Failed to export: ' + err, icon: 'error' })
      })
    },
    saveBlobResponse(res) {
      const disposition = res.headers && res.headers['content-disposition']
      const match = disposition && disposition.match(/filename="?([^";]+)"?/)
      const filename = match ? match[1] : 'staff_listing.xlsx'
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove()
      window.URL.revokeObjectURL(url)
    },
  },
}
</script>

<style scoped>
.table { font-size: 0.85rem; }
.filter-panel { background: #f8f8f8; }
.filter-label { font-size: 0.75rem; font-weight: 600; text-transform: uppercase; color: #6e6b7b; margin-bottom: 0.25rem; display: block; }
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
